import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Eye, EyeOff } from 'lucide-react';
import Layout from '../Layout';
import { postData } from '../../helpers/ApiSettings';
import { showToast } from '../../helpers/customToast';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    passwordConfirmation: ''
  });

  // Get the reset code from URL parameters
  const params = new URLSearchParams(location.search);
  const code = params.get('code');

  if (!code) {
    navigate('/forgot-password');
  }

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push(`Au moins ${minLength} caractères`);
    if (!hasUpperCase) errors.push('Une majuscule');
    if (!hasLowerCase) errors.push('Une minuscule');
    if (!hasNumbers) errors.push('Un chiffre');
    if (!hasSpecialChar) errors.push('Un caractère spécial');

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (!formData.password || !formData.passwordConfirmation) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (formData.password !== formData.passwordConfirmation) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setError(`Le mot de passe doit contenir : ${passwordErrors.join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      const response = await postData('/auth/reset-password', {
        code: code,
        password: formData.password,
        passwordConfirmation: formData.passwordConfirmation
      });

      showToast('Votre mot de passe a été réinitialisé avec succès', 'success');
      navigate('/connexion');
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.response?.data?.error?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Layout>
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-6">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <div className="text-center mb-4">
                  <h2 className="mb-1">Réinitialiser le mot de passe</h2>
                  <p className="text-muted">
                    Entrez votre nouveau mot de passe
                  </p>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Nouveau mot de passe</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <Lock size={18} />
                      </span>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="form-control"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Confirmer le mot de passe</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <Lock size={18} />
                      </span>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className="form-control"
                        name="passwordConfirmation"
                        value={formData.passwordConfirmation}
                        onChange={handleInputChange}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="text-muted small">
                      Le mot de passe doit contenir au moins :
                      <ul className="mb-0">
                        <li>8 caractères</li>
                        <li>Une majuscule</li>
                        <li>Une minuscule</li>
                        <li>Un chiffre</li>
                        <li>Un caractère spécial (!@#$%^&*)</li>
                      </ul>
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    ) : null}
                    Réinitialiser le mot de passe
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResetPasswordPage;